import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';
import { ClientLogger } from '../utils/logger';

/**
 * MSAL Configuration
 * Azure AD multi-tenant authentication for tenant portal
 */
const logger = new ClientLogger('AuthConfig');
const defaultTenantClientId = '26ff84ed-eb87-4de4-9554-6074e3fde46a';
const tenantClientId =
  import.meta.env.VITE_TENANT_CLIENT_ID ||
  (import.meta.env.DEV ? defaultTenantClientId : '');

if (!import.meta.env.VITE_TENANT_CLIENT_ID) {
  if (import.meta.env.DEV) {
    logger.warn('VITE_TENANT_CLIENT_ID is not set; using local development fallback client ID');
  } else {
    logger.error('VITE_TENANT_CLIENT_ID is not set; production deployments must use a dedicated customer portal app registration');
  }
}

const msalConfig: Configuration = {
  auth: {
    clientId: tenantClientId,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            logger.error(message);
            break;
          case LogLevel.Warning:
            logger.warn(message);
            break;
          case LogLevel.Info:
            logger.info(message);
            break;
          default:
            logger.debug(message);
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/**
 * Scopes for API access
 */
export const loginRequest = {
  // Keep first-run sign-in lightweight. Broad directory consent is only needed
  // when an admin opens User Management and explicitly grants it.
  scopes: ['User.Read'],
  claims: '{"access_token":{"roles":{"essential":true}}}', // Request roles in ID token
};

/**
 * Protected resource map (for acquiring tokens)
 */
export const protectedResources = {
  api: {
    endpoint: '/api',
    // Use User.Read scope — the bot service API reads only the tid claim from
    // the token, so a Graph token works. A SPA cannot request tokens for itself
    // (AADSTS90009), so api://<same-client-id>/.default is not valid here.
    scopes: ['User.Read'],
  },
  graph: {
    endpoint: 'https://graph.microsoft.com',
    scopes: ['User.Read.All'],
  },
};

/**
 * MSAL instance (singleton)
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL instance
 * Must be called before using MSAL
 */
export async function initializeMsal() {
  await msalInstance.initialize();

  // Handle redirect response (critical — without this, auth state is lost after redirect)
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      logger.info('Redirect login successful', { username: response.account?.username });
      msalInstance.setActiveAccount(response.account);
    }
  } catch (error) {
    logger.error('Error handling redirect', error);
  }

  // Set active account if there's only one
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 1) {
    msalInstance.setActiveAccount(accounts[0]);
  }
}
